import {
	addMonths,
	addQuarters,
	addYears,
	differenceInCalendarMonths,
	differenceInCalendarQuarters,
	differenceInCalendarYears,
	format,
	parse,
} from "date-fns";

const quarterToStartDate = (quarterString: string): Date => {
	const [year, quarter] = quarterString.split("-");
	const monthMap = { Q1: 0, Q2: 3, Q3: 6, Q4: 9 };
	return new Date(Number(year), monthMap[quarter], 1);
};

const monthStringToDate = (monthString: string): Date => {
	// Parse YYYY-MM format using date-fns to avoid timezone conversion issues
	return parse(monthString, "yyyy-MM", new Date());
};

// Create a time range based on the labels and datasets
export const fillTimeRange = (chartDatasets, rangeType) => {
	const { labels = [], datasets = [] } = chartDatasets;

	if (labels.length <= 1 || !rangeType) {
		return { labels, datasets };
	}

	// Special case for invalid labels: Remove the first label if it is null
	if (labels[0]._id === null && !labels[0].name) {
		labels.splice(0, 1);
		datasets.splice(0, 1);
	}

	// Sort labels chronologically to find the actual min and max dates
	const sortedIndices = labels
		.map((label, index) => ({ label, index }))
		.sort((a, b) => {
			const dateA =
				rangeType === "quarter"
					? quarterToStartDate(a.label.name as string)
					: rangeType === "month"
						? monthStringToDate(a.label.name as string)
						: new Date(a.label.name as string);
			const dateB =
				rangeType === "quarter"
					? quarterToStartDate(b.label.name as string)
					: rangeType === "month"
						? monthStringToDate(b.label.name as string)
						: new Date(b.label.name as string);
			return dateA.getTime() - dateB.getTime();
		});

	const firstLabel = sortedIndices[0].label;
	const lastLabel = sortedIndices[sortedIndices.length - 1].label;

	const startDate =
		rangeType === "quarter"
			? quarterToStartDate(firstLabel.name as string)
			: rangeType === "month"
				? monthStringToDate(firstLabel.name as string)
				: new Date(firstLabel.name as string);
	const endDate =
		rangeType === "quarter"
			? quarterToStartDate(lastLabel.name as string)
			: rangeType === "month"
				? monthStringToDate(lastLabel.name as string)
				: new Date(lastLabel.name as string);

	const dateRange = getLabelsRange(startDate, endDate, rangeType);

	// map the time labels to the dataset
	const labelsMap = labels.reduce((acc, label, index) => {
		acc[label.name as string] = { ...label, data: datasets[index] };
		return acc;
	}, {});

	// fill the time range with the datasets
	return {
		labels: dateRange.map((date) => labelsMap[date] || { name: date }),
		datasets: dateRange.map((date) => labelsMap[date]?.data),
	};
};

export const getLabelsRange = (startDate: Date, endDate: Date, rangeType: "month" | "year" | "quarter"): string[] => {
	switch (rangeType) {
		case "month":
			return generateMonthlyRange(startDate, endDate);

		case "year":
			return generateYearlyRange(startDate, endDate);

		case "quarter":
			return generateQuarterlyRange(startDate, endDate);

		default:
			throw new Error("Invalid range type. Choose month, year, or quarter.");
	}
};

const generateMonthlyRange = (startDate: Date, endDate: Date) => {
	const dateRange: string[] = [];
	const monthsRange = differenceInCalendarMonths(endDate, startDate) + 1;

	for (let i = 0; i < monthsRange; i++) {
		const currentMonth = addMonths(startDate, i);
		dateRange.push(format(currentMonth, "yyyy-MM"));
	}

	return dateRange;
};

const generateYearlyRange = (startDate: Date, endDate: Date) => {
	const dateRange: string[] = [];
	const yearsCount = differenceInCalendarYears(endDate, startDate) + 1;

	for (let i = 0; i < yearsCount; i++) {
		const currentYear = addYears(startDate, i);
		dateRange.push(format(currentYear, "yyyy"));
	}

	return dateRange;
};

const generateQuarterlyRange = (startDate: Date, endDate: Date) => {
	const dateRange: string[] = [];
	const quartersCount = differenceInCalendarQuarters(endDate, startDate) + 1;

	for (let i = 0; i < quartersCount; i++) {
		const currentQuarter = addQuarters(startDate, i);
		dateRange.push(`${format(currentQuarter, "yyyy")}-Q${Math.floor(currentQuarter.getMonth() / 3 + 1)}`);
	}

	return dateRange;
};
